import React, { memo, useMemo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { AemComponentEntity, ProductEntity } from 'constants/data-shapes/product-entity';

import ProductGroup from 'components/ProductGrid/Group';
import ProductList from 'components/ProductList';
import ProductGridItems from 'components/ProductGrid/ProductGridItems';
import ViewAlternativesProductModal from 'components/ProductGrid/ViewAlternativesProductModal';
import Spinner from '@johnlewispartnership/wtr-ingredients/ingredients/Spinner';
import { useLoading } from './Hooks/use-loading';
import styles from './ProductGrid.scss';

const productGridDefaultPropEmpty = () => null;
const productGridDefaultPropProducts = [];
const productGridDefaultPropResetError = () => {};

const ProductGrid = ({
  Empty = productGridDefaultPropEmpty,
  groupsTitleElement,
  groups,
  InfiniteScroll = null,
  isBuilderPage = false,
  listId,
  loading = true,
  LoadMoreButton = null,
  onDataLoad,
  orderId = null,
  products = productGridDefaultPropProducts,
  resetError = productGridDefaultPropResetError,
  searchType = null,
  taxonomyLevel = null,
  listDefinitions,
}) => {
  useEffect(() => {
    // Hide the error of a failed search when loading the view
    // example going from search to login and back
    resetError?.();
  }, [resetError]);

  const { showSpinner } = useLoading({ orderId, loading, onDataLoad });

  const [autoFocusIndex, setAutoFocusIndex] = useState();

  if (loading && typeof autoFocusIndex !== 'undefined') {
    setAutoFocusIndex(undefined);
  }

  const hasProducts = products.length > 0;

  const contents = useMemo(() => {
    if (showSpinner) {
      return <Spinner className="spinnerWrapper" isActive pageSpinner />;
    }

    if (!loading && !hasProducts) {
      return Empty ? <Empty /> : null;
    }

    const displayInGroups = groups && groups.size > 0;

    const contextProducts = groups
      ? [...groups.values()]
          .flat()
          .map(
            flattenProduct =>
              products.filter(
                product => product.searchProduct === flattenProduct.searchProduct,
              )[0] || {},
          )
      : null;

    const getLocalAutoFocusIndex = (offset, groupSize) => {
      const localIndex = autoFocusIndex - offset;
      return localIndex >= 0 && localIndex < groupSize ? localIndex : undefined;
    };

    let groupOffset = 0;
    const productListBody = displayInGroups ? (
      <div className="container-fluid">
        <ul className={styles.productGroups}>
          {[...groups.keys()].map(categoryId => {
            const groupProducts = groups.get(categoryId);
            const localAutoFocusIndex = getLocalAutoFocusIndex(groupOffset, groupProducts.length);
            groupOffset += groupProducts.length;
            return (
              <li className={styles.productGroup} data-testid="product-group" key={categoryId}>
                <ProductGroup
                  autoFocusIndex={localAutoFocusIndex}
                  categoryId={categoryId}
                  titleElement={groupsTitleElement}
                  listId={listId}
                  isBuilderPage={isBuilderPage}
                  orderId={orderId}
                  products={groupProducts}
                  contextProducts={contextProducts}
                  searchType={searchType}
                  taxonomyLevel={taxonomyLevel}
                  key={categoryId}
                  listDefinitions={listDefinitions}
                />
              </li>
            );
          })}
        </ul>
      </div>
    ) : (
      <ProductGridItems
        autoFocusIndex={autoFocusIndex}
        isBuilderPage={isBuilderPage}
        items={products}
        listId={listId}
        searchType={searchType}
        taxonomyLevel={taxonomyLevel}
        listDefinitions={listDefinitions}
      />
    );

    return (
      <>
        <ProductList
          {...{
            setAutoFocusIndex,
            displayInGroups,
            InfiniteScroll,
            loading,
            LoadMoreButton,
            withContainer: !displayInGroups,
          }}
        >
          {productListBody}
        </ProductList>
      </>
    );
  }, [
    Empty,
    autoFocusIndex,
    groups,
    hasProducts,
    InfiniteScroll,
    groupsTitleElement,
    isBuilderPage,
    listId,
    loading,
    LoadMoreButton,
    orderId,
    products,
    searchType,
    setAutoFocusIndex,
    showSpinner,
    taxonomyLevel,
    listDefinitions,
  ]);

  return (
    <>
      <div className={styles.products} data-testid="product-grid">
        {contents}
      </div>
      <ViewAlternativesProductModal listDefinitions={listDefinitions} />
    </>
  );
};

ProductGrid.displayName = 'ProductGrid';

ProductGrid.propTypes = {
  Empty: PropTypes.elementType,
  groups: PropTypes.oneOfType([PropTypes.instanceOf(Map), PropTypes.bool]),
  InfiniteScroll: PropTypes.elementType,
  groupsTitleElement: PropTypes.string,
  isBuilderPage: PropTypes.bool,
  listId: PropTypes.string,
  loading: PropTypes.bool,
  LoadMoreButton: PropTypes.elementType,
  orderId: PropTypes.string,
  products: PropTypes.arrayOf(PropTypes.oneOfType([AemComponentEntity(), ProductEntity()])),
  resetError: PropTypes.func,
  searchType: PropTypes.string,
  taxonomyLevel: PropTypes.string,
  onDataLoad: PropTypes.func,
  listDefinitions: PropTypes.shape({
    curation: PropTypes.string,
    format: PropTypes.string,
    page: PropTypes.string,
    title: PropTypes.string,
  }),
};

export default memo(ProductGrid);
